import React, { useEffect, useRef, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, label, btn, btnGhost } from "../drawings/_drawingsCommon";
import SyncStatusBadge from "./SyncStatusBadge";

const W = 1600, H = 1100;

export default function PIDEditor({ projectId }) {
  const [layout, setLayout] = useState(null);
  const [symbols, setSymbols] = useState(null);
  const [selected, setSelected] = useState(null);
  const [pendingDrop, setPendingDrop] = useState(null);   // {pal: 'equipment', sym: {...}}
  const [customTag, setCustomTag] = useState("");          // user-supplied tag override
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(null);          // {item, sx, sy, ox, oy}
  const svgRef = useRef();
  const dragSaveTimer = useRef(null);

  const load = async () => {
    try {
      const [l, s] = await Promise.all([
        api.get(`/projects/${projectId}/pid/layout`),
        api.get(`/projects/${projectId}/pid/symbols`),
      ]);
      setLayout(l.data);
      setSymbols(s.data);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { load(); }, [projectId]);

  // ─── Drag to move ────────────────────────────────────────────────
  const _svgPoint = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const sx = svgRef.current.viewBox.baseVal.width / rect.width;
    const sy = svgRef.current.viewBox.baseVal.height / rect.height;
    return { x: (clientX - rect.left) * sx, y: (clientY - rect.top) * sy };
  };
  const startDrag = (item, e) => {
    if (pendingDrop) return;
    e.stopPropagation();
    e.preventDefault();
    const p = _svgPoint(e.clientX, e.clientY);
    setSelected(item);
    setDragging({ item, sx: p.x, sy: p.y, ox: item.x || 0, oy: item.y || 0 });
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const p = _svgPoint(e.clientX, e.clientY);
      const nx = dragging.ox + (p.x - dragging.sx);
      const ny = dragging.oy + (p.y - dragging.sy);
      // Snap to 10-unit grid
      dragging.item.x = Math.round(nx / 10) * 10;
      dragging.item.y = Math.round(ny / 10) * 10;
      setLayout((l) => ({ ...l }));
    };
    const onUp = () => {
      const item = dragging.item;
      setDragging(null);
      // Debounced save
      clearTimeout(dragSaveTimer.current);
      dragSaveTimer.current = setTimeout(() => {
        api.put(`/projects/${projectId}/pid/items/${item.id}`,
                { patch: { x: item.x, y: item.y } }).catch(() => {});
      }, 150);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, projectId]);

  const handleCanvasClick = async (e) => {
    if (!pendingDrop || dragging) return;
    setBusy(true);
    const { x, y } = _svgPoint(e.clientX, e.clientY);
    try {
      const { pal, sym } = pendingDrop;
      const item = { type: pal === "valves" ? "valve" : (pal.replace(/s$/, "") || "equipment"),
                      symbol: sym.id, x, y,
                      tag: customTag.trim() || undefined,
                      properties: { tag_prefix: sym.tag_prefix } };
      const { data } = await api.post(`/projects/${projectId}/pid/items`, { item });
      setLayout((l) => ({ ...l, [_bucket(item.type)]: [...(l[_bucket(item.type)] || []), data.item],
                          validation: data.validation }));
      setPendingDrop(null);
      setCustomTag("");
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setBusy(false); }
  };

  const moveItem = async (item, dx, dy) => {
    const newX = (item.x || 0) + dx, newY = (item.y || 0) + dy;
    item.x = newX; item.y = newY;
    setLayout({ ...layout });
    try {
      await api.put(`/projects/${projectId}/pid/items/${item.id}`, { patch: { x: newX, y: newY } });
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete ${item.tag || item.id}?`)) return;
    try {
      const { data } = await api.delete(`/projects/${projectId}/pid/items/${item.id}`);
      setSelected(null);
      load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const importFromRun = async () => {
    if (!window.confirm("Import equipment, lines, and instruments from the latest AI run?\nExisting items will be kept (merge mode).")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/pid/import-from-run`,
        { mode: "merge" });
      await load();
      alert(`Imported ${data.summary.equipment} equipment · ${data.summary.lines} lines · ${data.summary.instruments} instruments`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setBusy(false); }
  };

  const syncBackToRun = async () => {
    if (!window.confirm("Push the current P&ID layout back to the latest run's pdo_enriched?\nMTO and Pipe Stress will then reflect manual edits (merge mode — existing run data is preserved).")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/pid/sync-back-to-run`,
        { mode: "merge" });
      const s = data.summary || {};
      const t = data.totals || {};
      alert(`Synced to run ${data.run_id}:\n` +
            `  Equipment +${s.equipment_added} · ↻${s.equipment_updated}\n` +
            `  Lines +${s.lines_added} · ↻${s.lines_updated}\n` +
            `  Instruments +${s.instruments_added} · ↻${s.instruments_updated}\n` +
            `\nRun now has ${t.equipment} equipment · ${t.lines} lines · ${t.instruments} instruments.`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    finally { setBusy(false); }
  };

  const validate = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/pid/validate`);
      setLayout((l) => ({ ...l, validation: data.validation }));
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const commitRev = async () => {
    const r = window.prompt("Revision letter / number:", "A");
    if (!r) return;
    const desc = window.prompt("Description:", "");
    try {
      await api.post(`/projects/${projectId}/pid/revisions`, { revision: r, description: desc || "" });
      load();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  if (!layout || !symbols) return <div style={{ padding: 24, color: "var(--pf-muted)" }}>Loading P&ID Editor…</div>;

  const allItems = [...(layout.equipment || []), ...(layout.instruments || []), ...(layout.inline || [])];
  const validation = layout.validation || [];
  const validationByItem = Object.fromEntries(validation.map((v) => [v.item_id, v.severity]));

  return (
    <div data-testid="pid-editor" style={{ display: "grid", gridTemplateColumns: "240px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* SHAPE PALETTE */}
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        {Object.entries(symbols).map(([palKey, items]) => (
          <div key={palKey} style={{ marginBottom: 10 }}>
            <div style={{ ...label, marginBottom: 4 }}>{palKey}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 3 }}>
              {items.map((s) => (
                <button key={s.id} data-testid={`pid-pal-${s.id}`}
                  onClick={() => setPendingDrop({ pal: palKey, sym: s })}
                  style={{
                    ...btnGhost, padding: "5px 8px", textAlign: "left", fontSize: 11,
                    background: pendingDrop?.sym?.id === s.id ? "rgba(242,107,31,.20)" : "transparent",
                    borderColor: pendingDrop?.sym?.id === s.id ? "#F26B1F" : "var(--pf-border)",
                  }}>{s.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* CANVAS */}
      <div style={{ ...card, padding: 0, overflow: "hidden", position: "relative" }}>
        <div style={{ display: "flex", gap: 6, padding: 6, background: "var(--pf-surface-2)", borderBottom: "1px solid var(--pf-border)", flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: pendingDrop ? "#F26B1F" : "var(--pf-muted)", fontWeight: 600 }}>
            {pendingDrop ? `Click on canvas to drop ${pendingDrop.sym.label}` :
              dragging ? "Dragging…" :
              "Pick a symbol from the palette →   |   Drag any placed item to move it"}
          </span>
          <div style={{ marginLeft: "auto" }}>
            <SyncStatusBadge projectId={projectId} editorName="pid_editor" />
          </div>
          {pendingDrop && (
            <input data-testid="pid-custom-tag" placeholder="Custom tag (optional)"
              value={customTag} onChange={(e) => setCustomTag(e.target.value)}
              style={{ width: 160, padding: "3px 6px", fontSize: 11, fontFamily: "monospace",
                       background: "#fff", color: "#222",
                       border: "1px solid var(--pf-border)", borderRadius: 4 }} />
          )}
          <span style={{ flex: 1 }} />
          <button data-testid="pid-import-run" style={btnGhost} onClick={importFromRun} disabled={busy}>
            📥 Import from latest run
          </button>
          <button data-testid="pid-sync-back-run" style={btnGhost} onClick={syncBackToRun} disabled={busy}>
            ↗ Sync to run
          </button>
          <button data-testid="pid-validate" style={btnGhost} onClick={validate}>Validate</button>
          <button data-testid="pid-commit-rev" style={btnGhost} onClick={commitRev}>Commit Rev.</button>
        </div>
        <svg ref={svgRef}
             data-testid="pid-canvas"
             viewBox={`0 0 ${W} ${H}`}
             onClick={handleCanvasClick}
             style={{ width: "100%", height: "calc(100% - 40px)", background: "#fafafa", cursor: pendingDrop ? "crosshair" : "default" }}>
          {/* Grid */}
          <defs>
            <pattern id="pidGrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e0e0e0" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width={W} height={H} fill="url(#pidGrid)" />

          {/* Lines */}
          {(layout.lines || []).map((ln) => (
            <PIDLine key={ln.id} line={ln} allItems={allItems} />
          ))}

          {/* Equipment / Inline / Instrument symbols */}
          {allItems.map((it) => (
            <PIDSymbol key={it.id} item={it} selected={selected?.id === it.id}
              severity={validationByItem[it.tag] || validationByItem[it.id]}
              onSelect={() => setSelected(it)}
              onDragStart={(e) => startDrag(it, e)} />
          ))}
        </svg>
      </div>

      {/* PROPERTIES + VALIDATION */}
      <div style={{ ...card, padding: 8, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
        {selected ? (
          <PropertiesEditor item={selected} projectId={projectId}
            onUpdate={(patch) => { Object.assign(selected, patch); setLayout({ ...layout }); }}
            onDelete={() => deleteItem(selected)} />
        ) : (
          <div>
            <div style={label}>Stats</div>
            <div style={{ fontSize: 11, color: "var(--pf-muted)" }}>
              Equipment: {(layout.equipment || []).length} ·
              Instruments: {(layout.instruments || []).length} ·
              Lines: {(layout.lines || []).length} ·
              Inline: {(layout.inline || []).length} ·
              Sheets: {(layout.sheets || []).length} ·
              Revisions: {(layout.revisions || []).length}
            </div>
          </div>
        )}

        <div>
          <div style={label}>Validation · {validation.length}</div>
          <div data-testid="pid-validation-list" style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto" }}>
            {validation.length === 0 && <div style={{ color: "#22c55e", fontSize: 11 }}>✓ No issues found</div>}
            {validation.map((v, i) => (
              <div key={i} style={{ padding: 6, fontSize: 10, borderRadius: 3,
                background: v.severity === "error" ? "rgba(239,68,68,.10)" : v.severity === "warning" ? "rgba(245,158,11,.10)" : "rgba(59,160,255,.10)",
                color: v.severity === "error" ? "#ef4444" : v.severity === "warning" ? "#f59e0b" : "#3aa0ff" }}>
                {v.message}
              </div>
            ))}
          </div>
        </div>

        {err && <div style={{ padding: 8, color: "#FF8888", background: "rgba(239,68,68,.1)", borderRadius: 4, fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}

function _bucket(t) {
  return { equipment: "equipment", valve: "inline", inline: "inline",
            instrument: "instruments", line: "lines" }[t] || "equipment";
}

function PIDSymbol({ item, selected, onSelect, severity, onDragStart }) {
  const x = item.x || 0, y = item.y || 0;
  const sevColor = severity === "error" ? "#ef4444" : severity === "warning" ? "#f59e0b" : null;
  const draw = () => {
    if ((item.symbol || "").startsWith("vessel-vert") || item.symbol === "column")
      return <rect x={-25} y={-50} width={50} height={100} rx={6} ry={6} fill="#fff" stroke="#222" strokeWidth={2} />;
    if (item.symbol === "vessel-horiz")
      return <rect x={-50} y={-25} width={100} height={50} rx={20} ry={20} fill="#fff" stroke="#222" strokeWidth={2} />;
    if ((item.symbol || "").startsWith("tank"))
      return <rect x={-40} y={-40} width={80} height={80} fill="#fff" stroke="#222" strokeWidth={2} />;
    if ((item.symbol || "").startsWith("pump"))
      return <circle r={25} fill="#fff" stroke="#222" strokeWidth={2} />;
    if (item.symbol === "compressor")
      return <polygon points="-25,-15 25,-25 25,25 -25,15" fill="#fff" stroke="#222" strokeWidth={2} />;
    if ((item.symbol || "").startsWith("hex") || item.symbol === "air-cooler")
      return <g><rect x={-30} y={-15} width={60} height={30} fill="#fff" stroke="#222" strokeWidth={2} />
                <line x1={-30} y1={0} x2={30} y2={0} stroke="#222" strokeWidth={1.5} /></g>;
    if (item.symbol === "fired-heater")
      return <g><rect x={-25} y={-25} width={50} height={50} fill="#fff" stroke="#222" strokeWidth={2} />
                <text x={0} y={5} fontSize={12} textAnchor="middle">F</text></g>;
    if (item.symbol === "flare")
      return <polygon points="-12,30 -2,-30 2,-30 12,30" fill="#fff" stroke="#222" strokeWidth={2} />;
    // Valves
    if ((item.symbol || "").includes("valve") || item.symbol === "psv")
      return <polygon points="-15,-12 15,-12 15,12 -15,12 0,0" fill="#fff" stroke="#222" strokeWidth={1.5} />;
    // Instruments — ISA bubble
    if (item.type === "instrument") {
      return <g>
        <circle r={18} fill="#fff" stroke="#222" strokeWidth={1.5} />
        <line x1={-18} y1={0} x2={18} y2={0} stroke="#222" strokeWidth={0.8} />
        <text x={0} y={-3} fontSize={9} textAnchor="middle" fontFamily="monospace">{(item.tag || "").split("-")[0]}</text>
        <text x={0} y={9} fontSize={8} textAnchor="middle" fontFamily="monospace" fill="#666">{(item.tag || "").split("-")[1] || ""}</text>
      </g>;
    }
    // Inline symbols
    if ((item.symbol || "").includes("flange"))
      return <line x1={0} y1={-8} x2={0} y2={8} stroke="#222" strokeWidth={3} />;
    if (item.symbol === "tee")
      return <g><line x1={-15} y1={0} x2={15} y2={0} stroke="#222" strokeWidth={2} />
                <line x1={0} y1={0} x2={0} y2={15} stroke="#222" strokeWidth={2} /></g>;
    return <rect x={-12} y={-12} width={24} height={24} fill="#fff" stroke="#666" strokeWidth={1.5} />;
  };
  return (
    <g transform={`translate(${x},${y}) rotate(${item.rotation || 0})`}
       onMouseDown={onDragStart}
       onClick={(e) => { e.stopPropagation(); onSelect(); }}
       data-testid={`pid-item-${item.id}`}
       style={{ cursor: "move" }}>
      {draw()}
      {selected && (
        <rect x={-55} y={-55} width={110} height={110} fill="none" stroke="#F26B1F" strokeWidth={2} strokeDasharray="4,2" />
      )}
      {item.tag && <text x={0} y={item.type === "instrument" ? 35 : 65} fontSize={11} textAnchor="middle" fontFamily="monospace" fill="#222">{item.tag}</text>}
      {sevColor && <circle cx={28} cy={-28} r={5} fill={sevColor} stroke="#fff" strokeWidth={1} />}
    </g>
  );
}

function PIDLine({ line, allItems }) {
  // Resolve from/to coordinates
  const resolve = (ep) => {
    if (typeof ep === "object" && ep?.x !== undefined) return ep;
    const tag = typeof ep === "string" ? ep : ep?.equipment;
    const found = allItems.find((it) => it.tag === tag || it.id === tag);
    return found ? { x: found.x, y: found.y } : null;
  };
  const a = resolve(line.from), b = resolve(line.to);
  if (!a || !b) return null;
  // Orthogonal route
  const mid = { x: b.x, y: a.y };
  return (
    <g data-testid={`pid-line-${line.id}`}>
      <polyline points={`${a.x},${a.y} ${mid.x},${mid.y} ${b.x},${b.y}`}
        fill="none" stroke="#222" strokeWidth={2} />
      <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 6} fontSize={9} fontFamily="monospace"
            textAnchor="middle" fill="#444">{line.id}</text>
    </g>
  );
}

function PropertiesEditor({ item, projectId, onUpdate, onDelete }) {
  const [draft, setDraft] = useState(JSON.stringify(item.properties || {}, null, 2));

  const save = async () => {
    try {
      const patch = { properties: JSON.parse(draft) };
      await api.put(`/projects/${projectId}/pid/items/${item.id}`, { patch });
      onUpdate(patch);
    } catch (e) { alert("Bad JSON or save failed: " + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div data-testid="pid-properties">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <strong style={{ color: "#F26B1F", fontSize: 13 }}>{item.tag || item.id}</strong>
        <button style={{ ...btnGhost, color: "#ef4444", padding: "3px 8px", fontSize: 10 }} onClick={onDelete} data-testid="pid-delete-btn">Delete</button>
      </div>
      <div style={{ fontSize: 11, color: "var(--pf-muted)", marginBottom: 6 }}>
        {item.type} · {item.symbol || "—"} · ({item.x?.toFixed(0)},{item.y?.toFixed(0)})
      </div>
      <div style={{ ...label, marginBottom: 4 }}>Properties (JSON)</div>
      <textarea data-testid="pid-properties-json"
        value={draft} onChange={(e) => setDraft(e.target.value)}
        style={{ width: "100%", minHeight: 140, padding: 6, fontSize: 10, fontFamily: "monospace",
                 background: "var(--pf-surface-2)", color: "var(--pf-fg)",
                 border: "1px solid var(--pf-border)", borderRadius: 3, boxSizing: "border-box" }} />
      <button data-testid="pid-save-props" style={{ ...btn, marginTop: 6, width: "100%" }} onClick={save}>Save Properties</button>
    </div>
  );
}
